import React, { useEffect, useReducer, useRef, useState } from "react";

import { Input, InputChangeEvent, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import { getInitialState } from "@selas/state-management";
import { EntityEditor, handleChange, IAddEntityScreenProps, IEditEntityScreenProps, YesNoSwitch } from "@selas/ui-components";
import { isNullOrEmpty } from "@selas/utils";
import { useTranslation } from "react-i18next";

import Endpoint from "../../../services/api/endpoint";
import { companyGroupReducer } from "../../../state/reducers";
import { ICompanyGroup } from "../../../utils/types/models";

const CompanyGroupEditor: React.FC<IAddEntityScreenProps<ICompanyGroup> | IEditEntityScreenProps<ICompanyGroup>> = (
	props: IAddEntityScreenProps<ICompanyGroup> | IEditEntityScreenProps<ICompanyGroup>
) => {
	const { t } = useTranslation();

	const [state, dispatch] = useReducer(companyGroupReducer, getInitialState<ICompanyGroup>());
	const initialCompanyGroup: ICompanyGroup = {
		id: (props as IEditEntityScreenProps<ICompanyGroup>).recordId || 0,
		name: "",
		active: true
	};
	const [companyGroup, setCompanyGroup] = useState<ICompanyGroup>(initialCompanyGroup);
	const [dataChanged, setDataChanged] = useState<boolean>(false);
	const firstField: React.MutableRefObject<Input> = useRef();

	useEffect(() => {
		if (state.entity) {
			setCompanyGroup(state.entity);
		}
	}, [state.entity]);

	function onChange(event: InputChangeEvent | SwitchChangeEvent): void {
		setCompanyGroup(handleChange(companyGroup, event));
		setDataChanged(true);
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(companyGroup.name)) {
			messages.push(t("fill_in_required_field", { field: t("name").toLowerCase() }));
		}
		return messages;
	}

	const readonly: boolean = (props as IEditEntityScreenProps<ICompanyGroup>).readonly;

	return (
		<EntityEditor
			width="70%"
			record={companyGroup}
			endpoint={Endpoint.CompanyGroups}
			entityState={state}
			entityType={t("companyGroup")}
			dispatch={dispatch}
			dataChanged={dataChanged}
			readonly={readonly}
			recordName={companyGroup.name}
			actionButtonClicked={props.actionButtonClicked}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				<label className="k-form-field">
					<span>{t("name")} *</span>
					<Input name="name" ref={firstField} className="full-width-field" value={companyGroup.name} onChange={onChange} required disabled={readonly} />
				</label>
				{!props.hideActive && (
					<div className="k-form-field">
						<div>{t("active")}</div>
						<YesNoSwitch name="active" checked={companyGroup.active} onChange={onChange} disabled={readonly} />
					</div>
				)}
			</div>
		</EntityEditor>
	);
};

export default CompanyGroupEditor;
